/**
 * useBackendSync Hook
 * 
 * Handles synchronization of assessment progress with the backend (MongoDB).
 * Works alongside localStorage for a dual-persistence strategy:
 * - localStorage: Fast, immediate, offline-capable
 * - Backend: Cross-device, persistent, authenticated
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AssessmentProgress } from '../types/assessment-progress';
import axios from 'axios';
import { getApiUrl } from '../config/environments';

/**
 * Create axios instance with auth token interceptor
 */
const createAuthenticatedAxios = (getToken: () => Promise<string | null>) => {
  const instance = axios.create({
    baseURL: getApiUrl()
  });

  // Add request interceptor to attach auth token
  instance.interceptors.request.use(
    async (config) => {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
};

interface UseBackendSyncOptions {
  debounceMs?: number;
  onSyncSuccess?: () => void;
  onSyncError?: (error: Error) => void;
  enabled?: boolean; // Allow disabling sync for testing
}

interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: Error | null;
  hasPendingChanges: boolean;
  retryCount: number;
}

export function useBackendSync(options: UseBackendSyncOptions = {}) {
  const {
    debounceMs = 1000,
    onSyncSuccess,
    onSyncError,
    enabled = true
  } = options;

  const { user, isLoading: isAuthLoading, isSignedIn, getToken } = useAuth();

  // Create authenticated axios instance
  const axiosInstance = useRef(createAuthenticatedAxios(getToken));

  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    isSyncing: false,
    lastSyncAt: null,
    syncError: null,
    hasPendingChanges: false,
    retryCount: 0
  });

  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastSyncedProgressRef = useRef<string>('');

  /**
   * Sync progress to backend (MongoDB)
   */
  const syncToBackend = useCallback(async (progress: AssessmentProgress): Promise<boolean> => {
    // Skip if sync is disabled
    if (!enabled) {
      console.log('⏸️ Backend sync disabled');
      return false;
    }

    // Skip if user is not authenticated
    if (!isSignedIn || !user) {
      console.log('⏸️ User not authenticated, skipping backend sync');
      setSyncStatus(prev => ({ ...prev, hasPendingChanges: false }));
      return false;
    }

    // Skip if progress hasn't changed
    const progressStr = JSON.stringify(progress);
    if (progressStr === lastSyncedProgressRef.current) {
      console.log('⏭️ Progress unchanged, skipping sync');
      return false;
    }

    setSyncStatus(prev => ({ ...prev, isSyncing: true, hasPendingChanges: false }));

    try {
      // Get token for logging (without exposing it)
      const token = await getToken();
      
      console.log('☁️ Syncing to backend...', {
        userId: user.id,
        step: progress.currentStep,
        sessionId: progress.sessionId,
        hasToken: !!token,
        tokenPrefix: token ? `${token.substring(0, 10)}...` : 'null'
      });

      // Map frontend progress structure to backend structure
      const backendProgress = {
        session_id: progress.sessionId,
        current_step: progress.currentStep,

        // Step 1
        company_name: progress.data.step1.companyName || null,
        industry_id: progress.data.step1.industry?.id || null,
        industry_name: progress.data.step1.industry?.name || null,
        industry_completed_at: progress.data.step1.completedAt || null,

        // Step 2
        role_id: progress.data.step2.role?.id || null,
        role_name: progress.data.step2.role?.name || null,
        role_completed_at: progress.data.step2.completedAt || null,

        // Step 3
        question_set: progress.data.step3.questionSet || null,
        ai_questions: progress.data.step3.aiQuestions || null,
        ai_answers: progress.data.step3.aiAnswers || {},
        ai_questions_generated_at: progress.data.step3.reviewedAt || null,

        // Step 4
        additional_questions: progress.data.step4.additionalQuestions || null,
        additional_answers: progress.data.step4.additionalAnswers || {},
        additional_questions_generated_at: progress.data.step4.completedAt || null,

        // Metadata
        question_regenerations: progress.data.step4.questionRegenerations || {},
        skipped_questions: progress.data.step4.skippedQuestions || [],
        last_saved: progress.lastSaved,
        expires_at: progress.expiresAt
      };

      // Use authenticated axios instance
      const response = await axiosInstance.current.post(
        '/assessments/progress',
        backendProgress
      );

      console.log('✅ Backend sync successful:', response.data);

      // Update sync status
      setSyncStatus({
        isSyncing: false,
        lastSyncAt: new Date(),
        syncError: null,
        hasPendingChanges: false,
        retryCount: 0
      });

      // Remember last synced progress
      lastSyncedProgressRef.current = progressStr;

      onSyncSuccess?.();
      return true;

    } catch (error: any) {
      console.error('❌ Backend sync error:', error);

      const errorObj = error instanceof Error ? error : new Error(String(error));

      setSyncStatus(prev => ({
        ...prev,
        isSyncing: false,
        syncError: errorObj,
        hasPendingChanges: true, // Mark as pending for retry
        retryCount: prev.retryCount + 1
      }));

      onSyncError?.(errorObj);

      // Retry with exponential backoff (max 3 retries)
      if (syncStatus.retryCount < 3) {
        const retryDelay = Math.pow(2, syncStatus.retryCount) * 1000; // 1s, 2s, 4s
        console.log(`🔄 Will retry in ${retryDelay}ms... (attempt ${syncStatus.retryCount + 1}/3)`);

        retryTimeoutRef.current = setTimeout(() => {
          syncToBackend(progress);
        }, retryDelay);
      } else {
        console.error('❌ Max retries reached, sync failed');
      }

      return false;
    }
  }, [enabled, isSignedIn, user, syncStatus.retryCount, onSyncSuccess, onSyncError]);

  /**
   * Sync with debounce (for text inputs)
   */
  const debouncedSync = useCallback((progress: AssessmentProgress) => {
    // Mark as pending
    setSyncStatus(prev => ({ ...prev, hasPendingChanges: true }));

    // Clear previous timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule sync
    syncTimeoutRef.current = setTimeout(() => {
      syncToBackend(progress);
    }, debounceMs);
  }, [debounceMs, syncToBackend]);

  /**
   * Sync immediately (no debounce)
   */
  const syncImmediate = useCallback((progress: AssessmentProgress) => {
    // Cancel any pending debounced sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncToBackend(progress);
  }, [syncToBackend]);

  /**
   * Load progress from backend
   */
  const loadFromBackend = useCallback(async (): Promise<AssessmentProgress | null> => {
    if (!enabled) {
      console.log('⏸️ Backend sync disabled');
      return null;
    }

    if (!isSignedIn || !user) {
      console.log('⏸️ User not authenticated, cannot load from backend');
      return null;
    }

    try {
      console.log('📥 Loading progress from backend...');

      // Use authenticated axios instance
      const response = await axiosInstance.current.get('/assessments/progress');

      if (!response.data || !response.data.progress) {
        console.log('ℹ️ No progress found in backend');
        return null;
      }

      const backendProgress = response.data.progress;

      // Map backend progress to frontend structure
      const frontendProgress: AssessmentProgress = {
        version: '1.0.0',
        sessionId: backendProgress.session_id,
        userId: user.id,
        lastSaved: backendProgress.last_saved,
        currentStep: backendProgress.current_step,
        expiresAt: backendProgress.expires_at,
        data: {
          step1: {
            companyName: backendProgress.company_name || '',
            industry: backendProgress.industry_id ? {
              id: backendProgress.industry_id,
              name: backendProgress.industry_name,
              description: '',
              icon: '',
              themeColor: '',
              roles: [] // Required by Industry type
            } : null,
            role: null,
            completedAt: backendProgress.industry_completed_at
          },
          step2: {
            role: backendProgress.role_id ? {
              id: backendProgress.role_id,
              name: backendProgress.role_name,
              description: '',
              industryId: backendProgress.industry_id || '', // Required by Role type
              tasks: []
            } : null,
            completedAt: backendProgress.role_completed_at
          },
          step3: {
            questionSet: backendProgress.question_set || null,
            aiQuestions: backendProgress.ai_questions || [],
            aiAnswers: backendProgress.ai_answers || {},
            reviewedAt: backendProgress.ai_questions_generated_at
          },
          step4: {
            additionalQuestions: backendProgress.additional_questions || [],
            additionalAnswers: backendProgress.additional_answers || {},
            questionRegenerations: backendProgress.question_regenerations || {},
            skippedQuestions: backendProgress.skipped_questions || [],
            progress: {
              answered: Object.keys(backendProgress.additional_answers || {}).length,
              total: (backendProgress.additional_questions || []).length
            },
            completedAt: backendProgress.additional_questions_generated_at
          },
          step5: {
            documents: [],
            lastUploadedAt: null
          }
        }
      };

      console.log('✅ Progress loaded from backend:', frontendProgress);
      lastSyncedProgressRef.current = JSON.stringify(frontendProgress);

      return frontendProgress;

    } catch (error: any) {
      console.error('❌ Error loading progress from backend:', error);

      // If 404 or no data, that's OK (means no progress exists)
      if (error.response?.status === 404 || !error.response) {
        return null;
      }

      return null;
    }
  }, [enabled, isSignedIn, user]);

  /**
   * Clear progress from backend
   */
  const clearBackend = useCallback(async (): Promise<boolean> => {
    if (!enabled) {
      console.log('⏸️ Backend sync disabled');
      return false;
    }

    if (!isSignedIn || !user) {
      console.log('⏸️ User not authenticated, cannot clear backend');
      return false;
    }

    try {
      console.log('🗑️ Clearing progress from backend...');

      // Use authenticated axios instance
      await axiosInstance.current.delete('/assessments/progress');

      console.log('✅ Backend progress cleared');
      lastSyncedProgressRef.current = '';
      setSyncStatus({
        isSyncing: false,
        lastSyncAt: null,
        syncError: null,
        hasPendingChanges: false,
        retryCount: 0
      });

      return true;

    } catch (error: any) {
      console.error('❌ Error clearing backend:', error);
      return false;
    }
  }, [enabled, isSignedIn, user]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  return {
    syncStatus,
    syncToBackend: debouncedSync,
    syncImmediate,
    loadFromBackend,
    clearBackend,
    isUserLoaded: !isAuthLoading,
    isUserAuthenticated: isSignedIn
  };
}

